import React, { useState, useRef, useEffect } from 'react';
import {
  FiMonitor, FiMessageSquare, FiBarChart2, FiZap, FiGift, FiUsers, FiGrid, FiVolume2,
  FiChevronDown, FiChevronUp, FiArrowRight, FiStar, FiLayers, FiShield
} from 'react-icons/fi';
import { useWebAppConfig } from '../context/webAppConfigContext';

interface VenuesSectionProps {
  onBookDemoClick: () => void;
}

const ICON_MAP: Record<string, React.ElementType> = {
  FiMonitor, FiMessageSquare, FiBarChart2, FiZap, FiGift, FiUsers, FiGrid, FiVolume2,
  FiStar, FiLayers, FiShield, FiArrowRight,
};

const DEFAULT_FEATURES = [
  {
    icon: 'FiMonitor',
    title: 'Live Event Screen',
    description: 'A branded, real-time event page your audience actually watches — with a live participant grid, activity feed, and an auto-cycling stage that showcases your brand.',
  },
  {
    icon: 'FiLayers',
    title: 'CRM Portal & Dashboard',
    description: 'Full business dashboard with scene control, live announcements, company management, promotion scheduling, and real-time analytics — all from one place.',
  },
  {
    icon: 'FiMessageSquare',
    title: 'Proximity Chat (ProxChats)',
    description: 'Real-time group chats that activate when people enter your space. Ephemeral, location-tied, and visible right on the event screen.',
  },
  {
    icon: 'FiGift',
    title: 'Promotions & Offers',
    description: 'Create and push promotions that appear on the live event stage and in attendee feeds. Time them to peak moments for maximum impact.',
  },
  {
    icon: 'FiStar',
    title: 'Business Showcase',
    description: 'An animated, auto-scrolling company spotlight with your logo, description, and website — designed to sell your brand to every attendee, every second.',
  },
  {
    icon: 'FiBarChart2',
    title: 'Real-Time Analytics',
    description: 'Live participant count, engagement metrics, dwell time, and demographic breakdown. This isn\'t social media analytics — this is real-world human behavior.',
  },
];

const DEFAULT_STATS = [
  { value: 'Real-Time', label: 'Participant tracking' },
  { value: '< 1s', label: 'Announcement delivery' },
  { value: '100%', label: 'Proximity accuracy' },
  { value: '24/7', label: 'Dashboard access' },
];

const DASHBOARD_CAPABILITIES = [
  'Scene Control — pin showcases, messages, promos',
  'Live Announcements — push to all viewers instantly',
  'Promotion Management — create, schedule, feature',
  'Company Registration & Approval',
  'Participant Analytics & Demographics',
  'Theme Customization',
  'Token-Based Access Control',
  'Real-Time Activity Feed',
];

const VenuesSection: React.FC<VenuesSectionProps> = ({ onBookDemoClick }) => {
  const { config } = useWebAppConfig();
  const v = config.venues;

  const features = v.features?.length ? v.features : DEFAULT_FEATURES;
  const stats = v.stats?.length ? v.stats : DEFAULT_STATS;

  const [expandedFeature, setExpandedFeature] = useState<number | null>(0);
  const [showDashboard, setShowDashboard] = useState(false);
  const featureRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setExpandedFeature(prev => {
        if (prev === null) return null;
        return (prev + 1) % features.length;
      });
    }, 6000);
    return () => clearInterval(interval);
  }, [features.length]);

  useEffect(() => {
    if (expandedFeature !== null && featureRefs.current[expandedFeature]) {
      featureRefs.current[expandedFeature]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [expandedFeature]);

  return (
    <div className="z-20 flex-shrink-0 py-8">
      <div className="relative min-h-[350px] lg:h-[calc(100vh-8rem)]">
        <div className="relative h-full flex flex-col">
          <div className="colorful-ring bg-BLACK/92 backdrop-blur-xl rounded-3xl p-6 lg:p-8 w-full flex-1 flex flex-col transition-colors shadow-2xl overflow-hidden">

            {/* Hero */}
            <div className="text-center mb-6">
              <h2 className="text-3xl lg:text-4xl font-bold text-WHITE mb-3">
                {v.headline}
              </h2>
              <div className="bg-WHITE/10 backdrop-blur-xl rounded-2xl px-6 py-3 mx-auto w-fit border border-WHITE/15 shadow-lg">
                <p
                  className="text-lg lg:text-xl font-bold leading-relaxed"
                  style={{
                    background: 'linear-gradient(90deg, #3b82f6 0%, #10b981 50%, #3b82f6 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {v.subheadline}
                </p>
              </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              {stats.map((stat, i) => (
                <div key={i} className="text-center px-3 py-3 rounded-xl bg-WHITE/5 border border-WHITE/10">
                  <div
                    className="text-xl lg:text-2xl font-black"
                    style={{
                      background: 'linear-gradient(135deg, #60a5fa, #34d399)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {stat.value}
                  </div>
                  <div className="text-WHITE/60 text-[11px] font-medium mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Feature Cards — scrollable */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-1 scrollbar-thin scrollbar-thumb-WHITE/20 scrollbar-track-transparent max-h-[380px] lg:max-h-[440px]">
              {features.map((feature, i) => {
                const IconComp = ICON_MAP[feature.icon || 'FiZap'] || FiZap;
                const isOpen = expandedFeature === i;
                return (
                  <div
                    key={i}
                    ref={el => { featureRefs.current[i] = el; }}
                    className="rounded-xl border transition-all duration-300 cursor-pointer"
                    style={{
                      background: isOpen ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.03)',
                      borderColor: isOpen ? 'rgba(96,165,250,0.4)' : 'rgba(255,255,255,0.08)',
                      boxShadow: isOpen ? '0 0 20px rgba(59,130,246,0.08)' : 'none',
                    }}
                    onClick={() => setExpandedFeature(isOpen ? null : i)}
                    onMouseEnter={() => setExpandedFeature(i)}
                  >
                    <div className="flex items-center gap-3 px-4 py-3">
                      <div
                        className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center"
                        style={{
                          background: isOpen ? 'rgba(59,130,246,0.2)' : 'rgba(255,255,255,0.06)',
                          border: `1px solid ${isOpen ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.1)'}`,
                        }}
                      >
                        <IconComp size={18} style={{ color: isOpen ? '#60a5fa' : 'rgba(255,255,255,0.5)' }} />
                      </div>
                      <h3 className={`flex-1 font-bold text-sm transition-colors ${isOpen ? 'text-WHITE' : 'text-WHITE/70'}`}>
                        {feature.title}
                      </h3>
                      {isOpen ? (
                        <FiChevronUp size={16} style={{ color: '#60a5fa' }} className="flex-shrink-0" />
                      ) : (
                        <FiChevronDown size={16} style={{ color: 'rgba(255,255,255,0.3)' }} className="flex-shrink-0" />
                      )}
                    </div>
                    {isOpen && (
                      <div className="px-4 pb-4">
                        <p className="text-WHITE/70 text-sm leading-relaxed pl-12">
                          {feature.description}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Dashboard Capabilities — collapsible */}
              <div
                className="rounded-xl border transition-all duration-300"
                style={{
                  background: showDashboard ? 'rgba(16,185,129,0.06)' : 'rgba(255,255,255,0.03)',
                  borderColor: showDashboard ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.08)',
                }}
              >
                <button
                  onClick={() => setShowDashboard(!showDashboard)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left"
                >
                  <div
                    className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center"
                    style={{
                      background: showDashboard ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.06)',
                      border: `1px solid ${showDashboard ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.1)'}`,
                    }}
                  >
                    <FiGrid size={18} style={{ color: showDashboard ? '#34d399' : 'rgba(255,255,255,0.5)' }} />
                  </div>
                  <h3 className={`flex-1 font-bold text-sm transition-colors ${showDashboard ? 'text-WHITE' : 'text-WHITE/70'}`}>
                    Full Dashboard Capabilities
                  </h3>
                  {showDashboard ? (
                    <FiChevronUp size={16} style={{ color: '#34d399' }} className="flex-shrink-0" />
                  ) : (
                    <FiChevronDown size={16} style={{ color: 'rgba(255,255,255,0.3)' }} className="flex-shrink-0" />
                  )}
                </button>
                {showDashboard && (
                  <div className="px-4 pb-4 pl-16">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                      {DASHBOARD_CAPABILITIES.map((cap, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm text-WHITE/70">
                          <FiZap size={12} className="mt-1 flex-shrink-0" style={{ color: '#34d399' }} />
                          <span>{cap}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Testimonial (CRM-configurable) */}
              {v.testimonialQuote && (
                <div className="rounded-xl px-5 py-4 border border-WHITE/10 bg-WHITE/5">
                  <p className="text-WHITE/80 text-sm italic leading-relaxed">
                    &ldquo;{v.testimonialQuote}&rdquo;
                  </p>
                  {v.testimonialAuthor && (
                    <p className="text-WHITE/50 text-xs mt-2 font-medium">— {v.testimonialAuthor}</p>
                  )}
                </div>
              )}
            </div>

            {/* CTA */}
            <button
              onClick={onBookDemoClick}
              className="group mt-5 w-full flex items-center justify-center gap-3 px-6 py-3.5 rounded-full text-base font-bold text-WHITE transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: 'linear-gradient(135deg, #3b82f6, #10b981)',
                boxShadow: '0 4px 20px rgba(59,130,246,0.3)',
              }}
            >
              {v.ctaText}
              <FiArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VenuesSection;
